import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { Card } from '@codes/ui-kit/components/ui/card';
import {
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationLink,
    PaginationNext,
    PaginationPrevious,
} from '@codes/ui-kit/components/ui/pagination';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { createAsyncDelay } from '@lemoncloud/lemon-web-core';
import { ToastTitle } from '@radix-ui/react-toast';
import { useQueryClient } from '@tanstack/react-query';
import { MemberCard } from '@web/app/features/styling/components/MemberCard';
import { usePagination, useSelectedStore } from '@web/app/shared';
import { Plus, UserPlus } from 'lucide-react';

import { useLoader } from '@codes/loader';
import { ownerQueryKeys, useInviteMember, useTeamMembers } from '@codes/owner';
import { Button, Dialog, DialogContent, DialogTitle, Input, Label, SharedSpinner } from '@codes/ui-kit';
import { useWebCoreStore } from '@codes/web-core';

export const MemberListPage = () => {
    const { t } = useTranslation();
    const { setIsLoading } = useLoader();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();

    const { workspace, project, setWorkspace, setProject } = useSelectedStore();
    const userId = useWebCoreStore(state => state.userId);
    const isOwner = workspace?.ownerId === userId;

    const page = parseInt(searchParams.get('page') || '0', 10);
    const limit = 12;
    const queryParams = {
        page,
        limit,
    };

    const { data: membersData, isLoading } = useTeamMembers(queryParams);

    const { paginationRange } = usePagination({
        totalCount: membersData?.total || 0,
        pageSize: limit,
        currentPage: page,
        siblingCount: 1,
    });

    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<'guest' | 'member'>('guest');
    const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
    const [emailError, setEmailError] = useState('');

    const inviteMember = useInviteMember();

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email) {
            setEmailError('Email is required');
            return false;
        }
        if (!emailRegex.test(email)) {
            setEmailError('Please enter a valid email address');
            return false;
        }
        setEmailError('');
        return true;
    };

    const handlePageChange = (newPage: number) => {
        setSearchParams(prev => {
            prev.set('page', newPage.toString());
            return prev;
        });
    };

    const handleInviteMember = async () => {
        if (!isOwner) {
            toast({
                title: <ToastTitle>{t('project.isNotOwner')}</ToastTitle>,
            });
            return;
        }

        if (!validateEmail(inviteEmail)) {
            return;
        }

        setIsLoading(true);
        try {
            await inviteMember.mutateAsync({
                email: inviteEmail,
                role: inviteRole,
            });
            await createAsyncDelay(1000);
            await queryClient.invalidateQueries(ownerQueryKeys.invalidateList());

            setIsInviteDialogOpen(false);
            setInviteEmail('');
            setInviteRole('guest');
            setEmailError('');
            toast({
                title: <ToastTitle>{t('project.invitationLinkSent')}</ToastTitle>,
            });
        } catch (error) {
            console.error('Failed to invite member:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex-1 w-full flex flex-col items-center h-full py-[34px] px-[68px] max-md:p-5 overflow-auto">
            <div className="flex-1 w-full">
                <div className="flex justify-between gap-y-4 flex-col items-start">
                    <div className="w-full flex items-center justify-between gap-4">
                        <span className="text-[22px] font-semibold">{t('project.allMember')}</span>
                        {isOwner && (
                            <Button
                                className="h-11 rounded-full gap-2 pl-[19px]"
                                onClick={() => setIsInviteDialogOpen(true)}
                            >
                                <Plus className="w-4 h-4" />
                                {t('project.addMember')}
                            </Button>
                        )}
                    </div>
                    <div className="text-[15px] text-point font-medium">
                        {t('project.total')} {membersData?.total} {t('project.totalMemberText')}
                    </div>
                </div>

                {/* Move the Dialog outside of the grid and make it reusable */}
                <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
                    <DialogContent className="p-6 pt-[42px]">
                        <DialogTitle className="text-title text-center">
                            {t('project.inviteMemberToProject')}
                        </DialogTitle>
                        <ul className="w-full mt-4 flex flex-col gap-6">
                            <li>
                                <Label variant="form">Invite by email</Label>
                                <div className="w-full flex flex-col gap-1">
                                    <div className="w-full flex items-center gap-2 max-xs:flex-col max-xs:gap-2">
                                        <div className="w-full flex-1">
                                            <Input
                                                type="email"
                                                placeholder="Invite others by email"
                                                value={inviteEmail}
                                                onChange={e => {
                                                    setInviteEmail(e.target.value);
                                                    if (emailError) {
                                                        validateEmail(e.target.value);
                                                    }
                                                }}
                                                className={emailError ? 'border-red-500' : ''}
                                            />
                                        </div>
                                    </div>
                                    {emailError && <span className="text-xs text-red-500">{emailError}</span>}
                                </div>
                            </li>
                            <li>
                                <Label variant="form">{t('project.setMemberRole')}</Label>
                                <div className="w-full flex items-center gap-2 max-xs:flex-col max-xs:gap-2">
                                    <div className="w-full flex-1">
                                        <Select onValueChange={setInviteRole} value={inviteRole}>
                                            <SelectTrigger>
                                                <SelectValue placeholder="Select a role" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="guest">View</SelectItem>
                                                <SelectItem value="member">Edit</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </li>
                        </ul>
                        <Button
                            size="lg"
                            className="p-5 w-full mt-[46px]"
                            onClick={handleInviteMember}
                            disabled={!inviteEmail || !inviteRole}
                        >
                            {t('project.sendInvitation')}
                        </Button>
                    </DialogContent>
                </Dialog>

                <div className="w-full mt-[42px]">
                    {isLoading ? (
                        <div className="w-full flex justify-center items-center">
                            <SharedSpinner />
                        </div>
                    ) : membersData?.data.length <= 1 ? (
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-xs:grid-cols-1 gap-[26px] max-md:gap-[18px]">
                            <Card
                                className="h-[162px] flex flex-col justify-center items-center gap-2 cursor-pointer"
                                onClick={() => setIsInviteDialogOpen(true)}
                            >
                                <UserPlus className="w-8 h-8" />
                                <div className="text-[18px] font-medium">{t('project.inviteMember')}</div>
                            </Card>
                        </div>
                    ) : (
                        <div className="grid grid-cols-[repeat(auto-fill,minmax(350px,1fr))] max-xs:grid-cols-1 gap-[26px] max-md:gap-[18px]">
                            {membersData?.data
                                .sort((a, b) => {
                                    if (a.$role?.role === 'owner') {
                                        return -1;
                                    }
                                    if (b.$role?.role === 'owner') {
                                        return 1;
                                    }
                                    return 0;
                                })
                                .map(member => (
                                    <MemberCard key={member.id} member={member} />
                                ))}
                        </div>
                    )}
                </div>
            </div>
            {!isLoading && membersData?.total !== 0 && (
                <div className="flex justify-center mt-auto">
                    <Pagination className="mt-[38px]">
                        <PaginationContent>
                            <PaginationItem>
                                <PaginationPrevious
                                    onClick={() => handlePageChange(page - 1)}
                                    aria-disabled={page <= 0}
                                    tabIndex={page <= 0 ? -1 : undefined}
                                    className={page <= 0 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                />
                            </PaginationItem>
                            {paginationRange.map((pageNumber, index) => (
                                <PaginationItem key={index}>
                                    {pageNumber === '...' ? (
                                        <PaginationEllipsis />
                                    ) : (
                                        <PaginationLink
                                            className="cursor-pointer"
                                            onClick={() => handlePageChange(Number(pageNumber))}
                                            isActive={page === Number(pageNumber)}
                                        >
                                            {Number(pageNumber) + 1}
                                        </PaginationLink>
                                    )}
                                </PaginationItem>
                            ))}
                            <PaginationItem>
                                <PaginationNext
                                    onClick={() => handlePageChange(page + 1)}
                                    disabled={page === Math.ceil((membersData?.total || 0) / limit) - 1}
                                    aria-disabled={page === Math.ceil((membersData?.total || 0) / limit) - 1}
                                    className={
                                        page === Math.ceil((membersData?.total || 0) / limit) - 1
                                            ? 'pointer-events-none opacity-50'
                                            : 'cursor-pointer'
                                    }
                                />
                            </PaginationItem>
                        </PaginationContent>
                    </Pagination>
                </div>
            )}
        </div>
    );
};
